import axios from 'axios'
import Future from 'fluture'
import QueryString from 'query-string'
import {
  assoc, evolve, map, mergeAll, path, pick, pipe,
  prop, pluck, toLower, zipObj,
} from 'ramda'
import {UKTime} from '@/format'
import {sequenceFuturesObject} from '@/store/future-helpers'
import {getURLWithoutPortfolioId} from './api-url-util'
import {storeSessionPartnerId, cleanPartnerId} from '@/partnerID'

const toTranslationsObject = (ts) => {
  return zipObj(
    pluck('resourceId', ts),
    pluck('trans', ts),
  )
}

const toUserObject = (data) => {
  return {
    ...data.data,
    serviceBalanceInternational: data.serviceBalanceInternational !== null ? Number(data.serviceBalanceInternational) : 0,
    serviceUsedInternational: data.serviceUsedInternational !== null ? Number(data.serviceUsedInternational) : 0,
    serviceSubscribedInternational: data.serviceSubscribedInternational !== null ? Number(data.serviceSubscribedInternational) : 0,
  }
}

export default (baseUrl, userReqData) => {
  const user = toUserObject(userReqData)
  storeSessionPartnerId(cleanPartnerId(userReqData.wlPartnerPseudoId))

  const onUser = (url) => `users/${user.userId}/${url}`
  const language = user.languageCode ? toLower(user.languageCode) : ''

  const token = sessionStorage.getItem('token')
  const base = axios.create({
    baseURL: baseUrl,
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  })
  const getF = Future.encaseP(base.get)
  const postF = Future.encaseP2(base.post)
  const patchF = Future.encaseP2(base.patch)
  const putF = Future.encaseP2(base.put)
  const deleteF = Future.encaseP(base.delete)

  const get = (url) =>
    getF(onUser(url))
      .map(prop('data'))

  const post = (url) => (data) =>
    postF(onUser(url), data)
      .map(prop('data'))

  const patch = (url) => (data) =>
    patchF(onUser(url), data)

  const put = (url) => (data) =>
    putF(onUser(url), data)

  const delete_ = (url) =>
    deleteF(onUser(url))

  const postExport = (url) => (data) =>
    postF(`${onUser(url)}?name=${data.name}`, data)
      .map(prop('data'))

  const translations =
    Future.of((language === '' || language === 'en') ? [] : [language])
      .map(map((l) => getF(`translations?locale=${l}`)))
      .chain(Future.parallel(2))
      .map(map(path(['data', 'data'])))
      .map(map(toTranslationsObject))
      .map(mergeAll)

  const getRuleInfo = (portfolioId) =>
    get(`portfolios/${portfolioId}/eventRules`)

  const getExports = (portfolioId, scheduledParams, previousParams) =>
    sequenceFuturesObject({
      scheduled: get(`portfolios/${portfolioId}/exports?${QueryString.stringify(scheduledParams)}`).map(pick(['data', 'totalCount'])),
      previous: get(`portfolios/${portfolioId}/exports/history?${QueryString.stringify(previousParams)}`).map(pick(['data', 'totalCount'])),
    })

  return {
    fetchUser: () =>
      Future.of(user),

    fetchPreferences: () =>
      get('preferences')
        .map(prop('data')),

    savePreferences: pipe(
      patch('preferences'),
      map(prop('data')),
    ),

    saveLanguagePreference: pipe(
      patch('preferences/language'),
      map(prop('data'))
    ),

    fetchPortfolios: (params) =>
      get(`portfolios?${QueryString.stringify(params)}`)
        .map(prop('data')),

    addPortfolio: pipe(
      assoc('emailLanguage', language),
      post('portfolios'),
      map(prop('data')),
    ),

    fetchActivePortfolio: (portfolioId) =>
      get(`portfolios/${portfolioId}/notificationSettings`)
        .map(prop('data')),

    updatePortfolio: (portfolioId) => pipe(
      assoc('emailLanguage', language),
      patch(`portfolios/${portfolioId}`)
    ),

    deletePortfolio: (portfolioId) =>
      delete_(`portfolios/${portfolioId}`),

    fetchPortfolioCompanies: (portfolioId, params, tab, lang) => {
      let req
      if (lang === 'JA') {
        params = {...params, language: 'JA'}
      }
      if (tab === 'analysis') {
        params = {...params, usage: 'analysis'}
      }
      req = get(getURLWithoutPortfolioId(`companies?${QueryString.stringify(params)}`, portfolioId))
      return req
        .map(pick(['data', 'totalCount', 'serviceUsedInternational', 'serviceSubscribedInternational']))
    },

    copyPortfolioCompanies: ({portfolios, companies, isAll, currentPortfolioId}) => {
      const payload = {
        portfolios: portfolios,
        companies: isAll ? [] : companies,
      }
      return isAll ? post(`portfolios/${currentPortfolioId}/companies/copy?copyAll=true`)(payload)
        : post(`portfolios/${currentPortfolioId}/companies/copy`)(payload)
    },

    movePortfolioCompanies: ({portfolios, companies, isAll, currentPortfolioId}) => {
      const payload = {
        portfolios: portfolios,
        companies: isAll ? [] : companies,
      }
      return isAll ? post(`portfolios/${currentPortfolioId}/companies/remove?removeAll=true`)(payload)
        : post(`portfolios/${currentPortfolioId}/companies/remove`)(payload)
    },

    deletePortfolioCompanies: ({companies, isAll, currentPortfolioId}) => {
      const payload = {
        companies: isAll ? [] : companies,
      }
      return isAll ? patch(`portfolios/${currentPortfolioId}/companies/clear?clearAll=true`)(payload)
        : patch(`portfolios/${currentPortfolioId}/companies/clear`)(payload)
    },

    savePortfolioCompany: (portfolioId, companyId) => pipe(
      assoc('safeNumber', companyId),
      (portfolioId === 0) ? patch(`companies/${companyId}`) : patch(`portfolios/${portfolioId}/companies/${companyId}`),
    ),

    getCompanyReportLink: ({companyId}) =>
      get(`companies/${companyId}/webReportLink`)
        .map(prop('data')),

    fetchPortfolioCountries: (portfolioId) => (portfolioId === 0)
      ? get(`portfolios/countries`).map(prop('data'))
      : get(`portfolios/${portfolioId}/countries`)
        .map(prop('data')),

    fetchPortfolioEvents: (portfolioId, params) => {
      const base = getURLWithoutPortfolioId(`notificationEvents`, portfolioId)
      const paramString = pipe(
        evolve({
          startDate: (x) => UKTime(x).format(),
          endDate: (x) => UKTime(x).format(),
        }),
        QueryString.stringify,
      )(params)

      const req = paramString
        ? get(`${base}?${paramString}`)
        : get(base)

      return req
        .map(pick(['data', 'totalCount']))
    },

    fetchPortfolioEventsSummary: (portfolioId) =>
      get(getURLWithoutPortfolioId(`notificationEvents/summary`, portfolioId))
        .map(prop('data')),

    fetchPortfolioEventsGraphData: ({portfolioId, startDate, endDate}) => {
      const paramString = QueryString.stringify({
        startDate: UKTime(startDate).format(),
        endDate: UKTime(endDate).format(),
      })

      return get(getURLWithoutPortfolioId(`notificationEvents/dataByDay?${paramString}`, portfolioId))
        .map(prop('data'))
        .map((data) => ({
          startDate,
          endDate,
          data,
        }))
    },

    fetchPortfolioExports: (portfolioId, scheduledParams, previousParams) =>
      getExports(portfolioId, scheduledParams, previousParams),

    fetchScheduledExports: (portfolioId, params) =>
      get(`portfolios/${portfolioId}/exports?${QueryString.stringify(params)}`),

    fetchPreviousExports: (portfolioId, params) =>
      get(`portfolios/${portfolioId}/exports/history?${QueryString.stringify(params)}`),

    deletePortfolioExport: ({portfolioId, name}) =>
      delete_(`portfolios/${portfolioId}/exports?name=${name}`),

    addPortfolioExport: (portfolioId) =>
      postExport(`portfolios/${portfolioId}/exports`),

    savePortfolioExport: (portfolioId, name) =>
      patch(`portfolios/${portfolioId}/exports?name=${name}`),

    fetchPortfolioExportAttachment: (portfolioId) => (params) =>
      get(`portfolios/${portfolioId}/exports/history/download?${QueryString.stringify(params)}`),

    fetchPortfolioRules: (portfolioId) =>
      getRuleInfo(portfolioId)
        .map(prop('data')),

    fetchPortfolioRiskSummary: (portfolioId) => get(getURLWithoutPortfolioId(`riskSummary`, portfolioId))
      .map(prop('data')),

    savePortfolioRules: ({portfolioId, countryCode}) =>
      put(`portfolios/${portfolioId}/eventRules/${countryCode}`),

    importCompaniesFile: (portfolioId) =>
      post(`portfolios/${portfolioId}/import`),

    syncCompaniesFile: (portfolioId) =>
      post(`portfolios/${portfolioId}/sync`),

    fetchTranslations: (lang) => lang ? getF(`translations?locale=${lang}`)
      .map(path(['data', 'data'])).map(toTranslationsObject) : translations,

    fetchNotifications: ({startDate, endDate, paging}) => {
      const paramString = QueryString.stringify({
        startDate: UKTime(startDate).format(),
        endDate: UKTime(endDate).format(),
        ...paging,
      })
      return get(`notifications?${paramString}`)
        .map(prop('data'))
        .map((data) => ({
          startDate,
          endDate,
          data,
        }))
    },

    getHomePageLink: () =>
      get(`homePage`)
        .map(prop('data')),

    fetchCustomerUsers: (portfolioId) =>
      get(`portfolios/${portfolioId}/sharingPermissions`),

    updateCustomerUsersPermissions: (portfolioId) =>
      patch(`portfolios/${portfolioId}/sharingPermissions`),
  }
}



// WEBPACK FOOTER //
// ./src/api.js